import type { ReadonlyDeep } from 'type-fest';
import type { InitialData as DispatchDriverSearchFormInitialData } from '@/components/searchForms/DispatchDriverSearchForm.vue';
import type { DataItem as DispatchDriversTableDataItem } from '@/components/tables/DispatchDriversTable.vue';
import type * as openapi from '@/helpers/api/types/openapiOverrides';

import _cloneDeep from 'lodash/cloneDeep';

import * as dispatchDriverJobsCustomTableHelpers from '@/helpers/tables/dispatchDriverJobsCustom';
import * as commonConstants from '@/constants/common';

import colors from '@/styles/colors.module.scss';

export function getTableItem(
  dispatchDriver: ReadonlyDeep<openapi.GetCatalogDrivers_200[0]>,
  allocatedDispatchJobs: ReadonlyDeep<openapi.DespatchJobsContentOpenapi>,
  dispatchDriversMarks: ReadonlyDeep<Record<number, string>>,
): DispatchDriversTableDataItem {
  const driverJobs = allocatedDispatchJobs.filter(function (job) {
    const driverCode = job.stops.at(-1)?.driver_is;
    return dispatchDriver.code === driverCode;
  });

  return {
    id: dispatchDriver.id || NaN,
    status:
      dispatchDriversMarks[dispatchDriver.code || NaN] ?? colors.colorGray200,
    driver: dispatchDriver.code || NaN, // TODO what are they `driver.id`, `driver.driver`, `driver_id`, `driver.code`, `driverCode`, `driver_is`?
    name: dispatchDriver.full_name || commonConstants.EM_DASH,
    jobCount: driverJobs.length,
    type: dispatchDriver.type || commonConstants.EM_DASH,
    notes: commonConstants.EM_DASH, // TODO
    fold: {
      dispatchDriverJobsCustomTableData:
        dispatchDriverJobsCustomTableHelpers.getTableData(driverJobs),
    },
  };
}

export function filterDispatchDriversBySearch(
  filteredDispatchDrivers: ReadonlyDeep<openapi.GetCatalogDrivers_200>,
  search: string,
): ReadonlyDeep<openapi.GetCatalogDrivers_200> {
  search = search.toUpperCase();
  return filteredDispatchDrivers.filter(function (dispatchDriver) {
    return (
      dispatchDriver.code.toString().toUpperCase().includes(search) ||
      dispatchDriver.full_name.toUpperCase().includes(search) ||
      dispatchDriver.type?.toUpperCase().includes(search)
    );
  });
}

export function filterDispatchDriversByFleets(
  filteredDispatchDrivers: ReadonlyDeep<openapi.GetCatalogDrivers_200>,
  fleets: readonly string[],
): ReadonlyDeep<openapi.GetCatalogDrivers_200> {
  return filteredDispatchDrivers.filter(function (dispatchDriver) {
    if (!dispatchDriver.department) return false;
    return fleets.includes(dispatchDriver.department);
  });
}

export function filterDispatchDriversByFiltersType(
  filteredDispatchDrivers: ReadonlyDeep<openapi.GetCatalogDrivers_200>,
  type: readonly string[],
): ReadonlyDeep<openapi.GetCatalogDrivers_200> {
  return filteredDispatchDrivers.filter(function (dispatchDriver) {
    if (!dispatchDriver.type) return false;
    return type.includes(dispatchDriver.type);
  });
}

export function getTableData(
  dispatchDrivers: ReadonlyDeep<openapi.GetCatalogDrivers_200> | undefined,
  dispatchJobs: ReadonlyDeep<openapi.DespatchJobsContentOpenapi> | undefined,
  dispatchDriverSearchFormInitialData: ReadonlyDeep<DispatchDriverSearchFormInitialData>, // or maybe pass queries here?
  dispatchDriversMarks: ReadonlyDeep<Record<number, string>> | undefined,
): DispatchDriversTableDataItem[] {
  const _dispatchDriversMarks = dispatchDriversMarks || {};
  const _dispatchDriversTableData: DispatchDriversTableDataItem[] = [];
  if (!dispatchDrivers?.length) return _dispatchDriversTableData;

  const allocatedDispatchJobs =
    dispatchJobs?.filter(function (dispatchJob) {
      return dispatchJob.allocation_state === 'allocated';
    }) || [];

  let filteredDispatchDrivers = _cloneDeep(dispatchDrivers);

  if (dispatchDriverSearchFormInitialData.search) {
    filteredDispatchDrivers = filterDispatchDriversBySearch(
      filteredDispatchDrivers,
      dispatchDriverSearchFormInitialData.search,
    );
  }

  if (dispatchDriverSearchFormInitialData.fleets?.length) {
    filteredDispatchDrivers = filterDispatchDriversByFleets(
      filteredDispatchDrivers,
      dispatchDriverSearchFormInitialData.fleets,
    );
  }

  if (dispatchDriverSearchFormInitialData.driverFilters.type?.length) {
    filteredDispatchDrivers = filterDispatchDriversByFiltersType(
      filteredDispatchDrivers,
      dispatchDriverSearchFormInitialData.driverFilters.type,
    );
  }

  filteredDispatchDrivers.forEach(function (dispatchDriver) {
    _dispatchDriversTableData.push(
      getTableItem(
        dispatchDriver,
        allocatedDispatchJobs,
        _dispatchDriversMarks,
      ),
    );
  });
  return _dispatchDriversTableData;
}
